import { useCallback, useEffect, useRef, useState } from 'react';
import { StanCrop } from '@/components';
import { StanFileUpload, StanFileUploadRef } from '@/components/core-components/StanFileUpload/StanFileUpload';
import { stanNotify } from '@/lib/stanNotify';
import { useUser } from '@/queries/useUser';
import { useProfileAvatarUpload } from '../hooks/useProfileAvatarUpload';

// Import SVG icon from assets
import IcPencil from '@/assets/icons/ic-edit-01.svg';
import { cn } from '@/lib';

export function ProfileAvatar() {
    const { user } = useUser();
    const fileUploadRef = useRef<StanFileUploadRef>(null);
    const [pickedFile, setPickedFile] = useState<File | null>(null);
    const [isCropOpen, setIsCropOpen] = useState(false);
    const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string>('');

    const { handleAvatarCropped, isUploading } = useProfileAvatarUpload(pickedFile?.name);

    // Update avatar when user data changes
    useEffect(() => {
        if (user?.profile_image) {
            setCurrentAvatarUrl(user.profile_image);
        }
    }, [user?.profile_image]);

    const displayAvatar = currentAvatarUrl;
    const userName = user?.full_name;

    const openFilePicker = useCallback(() => fileUploadRef.current?.onPickFile(), []);

    const onFilePicked = useCallback((files: File[]) => {
        const file = files[0] || null;
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            stanNotify({
                type: 'error',
                title: 'Invalid File Type',
                text: 'Please select an image file.',
                duration: 3000,
            });
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            stanNotify({
                type: 'error',
                title: 'File Too Large',
                text: 'File size must be less than 5MB.',
                duration: 5000,
            });
            return;
        }
        setPickedFile(file);
        setIsCropOpen(true);
    }, []);

    // Handle avatar cropped and upload
    const onAvatarCropped = useCallback(
        async (params: { croppedBlob: Blob; croppedDataUrl: string }) => {
            // Dismiss the crop component immediately
            setIsCropOpen(false);

            try {
                // Upload and update profile
                await handleAvatarCropped(params);

                // Clear picked file after successful upload
                setPickedFile(null);
                fileUploadRef.current?.clearFile();
            } catch {
                // Error handling is done in the hook
                setPickedFile(null);
                fileUploadRef.current?.clearFile();
            }
        },
        [handleAvatarCropped]
    );

    const handleCropClose = useCallback(() => {
        setIsCropOpen(false);
        setPickedFile(null);
        // Clear the file input when crop is cancelled
        fileUploadRef.current?.clearFile();
    }, []);

    return (
        <>
            <div className='flex flex-col items-center gap-4'>
                <div className='flex flex-col items-center gap-2'>
                    <button
                        className='group relative cursor-pointer'
                        onClick={!isUploading ? openFilePicker : undefined}
                        tabIndex={0}
                        disabled={isUploading}
                        onKeyDown={e => {
                            if (!isUploading && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                openFilePicker();
                            }
                        }}
                    >
                        <div className='bg-neutral-subtle relative flex size-24 items-center justify-center overflow-hidden rounded-full transition-opacity group-hover:opacity-80'>
                            {/* Loading overlay */}
                            {isUploading && (
                                <div className='bg-neutral-black/30 absolute inset-0 z-10 flex items-center justify-center rounded-full'>
                                    <div className='border-neutral-white size-8 animate-spin rounded-full border-2 border-t-transparent' />
                                </div>
                            )}

                            {/* TODO: make this into the ImagePreview component when its done in core components */}
                            {displayAvatar ? (
                                <img
                                    src={displayAvatar}
                                    alt='Profile'
                                    className={cn('h-full w-full object-cover', isUploading && 'brightness-50')}
                                />
                            ) : (
                                <div className='text-heading-1 text-brand-main font-bold'>
                                    {userName?.charAt(0) || 'U'}
                                </div>
                            )}
                        </div>

                        <div
                            className='pointer-events-none absolute right-0 -bottom-1 transition-transform group-hover:scale-110'
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                onClick={openFilePicker}
                                className='bg-brand-main grid size-6 place-items-center rounded-full'
                                disabled={isUploading}
                            >
                                <IcPencil className='size-5 fill-white' />
                            </button>
                        </div>
                    </button>
                    {isUploading && (
                        <div className='text-body-sm text-gray text-center'>Uploading and updating profile...</div>
                    )}
                </div>
            </div>

            <StanFileUpload
                ref={fileUploadRef}
                accept='image/png,image/jpeg,image/jpg,image/webp'
                confirmUpload={false}
                onFilePicked={onFilePicked}
            />

            {/* Standalone crop modal */}
            <StanCrop
                isOpen={isCropOpen}
                imageFile={pickedFile}
                aspectRatio={1}
                width={600}
                height={600}
                borderRadius='circle'
                onClose={handleCropClose}
                onConfirm={onAvatarCropped}
            />
        </>
    );
}
